import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tile } from "@/components/game/Tile";
import { Calendar, Trophy, Zap, Archive, Smartphone, Share2 } from "lucide-react";
import { SEOHead } from "@/components/seo/SEOHead";
import { getWebApplicationSchema, getFAQSchema, getBreadcrumbSchema } from "@/components/seo/structuredData";
import { Footer } from "@/components/layout/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { WordPopLogo } from "@/components/ui/WordPopLogo";
import { ResumeGameBanner } from "@/components/game/ResumeGameBanner";
import { usePWA } from "@/hooks/usePWA";
import { useState } from "react";
import { InstallModal } from "@/components/pwa/InstallModal";
import { trackPWAEvent } from "@/utils/pwaAnalytics";
import { ColorLegend } from "@/components/game/ColorLegend";

const faqData = [
  {
    question: "Is WordPop free to play?",
    answer: "Yes! WordPop is completely free with no subscriptions or hidden costs. The game is supported by non-intrusive advertising."
  },
  {
    question: "How do streaks work?",
    answer: "Your streak tracks consecutive days of successful puzzle solving. If you solve today's puzzle, your streak increases. Miss a day, and it resets to zero."
  },
  {
    question: "Why isn't my puzzle syncing?",
    answer: "Create a free account to enable cloud sync across devices. Without an account, progress is stored locally in your browser."
  },
  {
    question: "How do I install WordPop?",
    answer: "WordPop is a Progressive Web App (PWA). On iPhone, tap Share > Add to Home Screen. On Android, tap the menu and select Install App. Visit our Install page for detailed instructions."
  },
  {
    question: "Does it work offline?",
    answer: "Yes! Once installed as a PWA, WordPop works completely offline. Your progress syncs when you reconnect."
  },
  {
    question: "How do I share my results?",
    answer: "After completing a puzzle, tap the Share button to copy your colored grid. You can paste it anywhere to show off your solving skills without spoiling the answer."
  },
  {
    question: "What's the difference between WordPop and other word games?",
    answer: "WordPop offers 6 tries with an integrated hint system, hard mode, complete archive access, and cloud sync for a seamless experience across all your devices."
  },
  {
    question: "Can I play past puzzles?",
    answer: "Absolutely! Visit the Archive to play any previous daily puzzle. Perfect for practice or catching up on missed days."
  }
];

const structuredData = [
  getWebApplicationSchema(),
  getFAQSchema(faqData),
  getBreadcrumbSchema([
    { name: "Home", url: "https://wordpop.games" }
  ])
];

export default function HomeLanding() {
  const { canInstall, isInstalled } = usePWA();
  const [showInstallModal, setShowInstallModal] = useState(false);

  const handleInstallClick = () => {
    trackPWAEvent('install_clicked', 'home_landing', canInstall, isInstalled);
    setShowInstallModal(true);
  };

  return (
    <>
      <SEOHead
        title="WordPop - Daily Word Puzzle Game"
        description="Guess the hidden 5-letter word in 6 tries. Play the free daily word puzzle game with hints, streaks, cloud sync, and archive access. New puzzle every day!"
        canonicalUrl="https://wordpop.games"
        structuredData={structuredData}
      />

      <div className="min-h-screen flex flex-col bg-background">
        {/* Hero Section */}
        <header className="bg-gradient-to-b from-primary/10 via-background to-background border-b border-border">
          <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="mb-4">
              <WordPopLogo linkTo={null} size="lg" />
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8">
              A smarter, cleaner daily word challenge
            </p>
            <Link to="/play">
              <Button size="lg" className="text-lg px-8 py-6">
                Play Today's Puzzle →
              </Button>
            </Link>
          </div>
        </header>

        {/* Resume Game Banner (appears when user has in-progress archive puzzle) */}
        <ResumeGameBanner />

        <main className="flex-1 max-w-5xl mx-auto px-4 py-12 space-y-16">
          {/* What is WordPop */}
          <section>
            <h2 className="text-3xl font-bold text-foreground mb-6">What is WordPop?</h2>
            <div className="text-lg text-muted-foreground space-y-4 leading-relaxed">
              <p>
                WordPop is a daily word puzzle game that challenges you to guess a five-letter word in just six attempts. 
                Every day at midnight, a new puzzle becomes available for players worldwide.
              </p>
              <p>
                Use color-coded feedback to narrow down the solution: pink tiles show correct letters in the right position, 
                purple tiles indicate correct letters in the wrong position, and brown tiles eliminate letters entirely. 
                It's simple to learn but challenging to master.
              </p>
            </div>
          </section>

          {/* How to Play */}
          <section>
            <h2 className="text-3xl font-bold text-foreground mb-6">How to Play</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">Step 1: Enter Your First Guess</h3>
                  <p className="text-muted-foreground">
                    Type any valid five-letter word. Choose words with common vowels and frequently-used consonants like 
                    R, S, T, L, and N. Popular starting words include CRANE, SLATE, or STARE.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">Step 2: Read the Color Feedback</h3>
                  <div className="flex gap-2 my-3">
                    <Tile char="W" status="correct" />
                    <Tile char="O" status="present" />
                    <Tile char="R" status="absent" />
                    <Tile char="D" status="absent" />
                    <Tile char="S" status="absent" />
                  </div>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>🩷 <strong>Pink:</strong> Letter is correct and in the right position</li>
                    <li>🟪 <strong>Purple:</strong> Letter is in the word but wrong position</li>
                    <li>🤎 <strong>Brown:</strong> Letter is not in the word at all</li>
                  </ul>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">Step 3: Refine Your Strategy</h3>
                  <p className="text-muted-foreground">
                    Use the feedback to narrow down possibilities. Eliminate brown letters, reposition purple letters, 
                    and build on pink letters. Each guess should teach you something new.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">Step 4: Solve in 6 Tries</h3>
                  <p className="text-muted-foreground">
                    You have six attempts to find the solution. Use strategic thinking, pattern recognition, and 
                    vocabulary knowledge to crack the code before running out of guesses.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Strategies */}
          <section>
            <h2 className="text-3xl font-bold text-foreground mb-6">Winning Strategies</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-primary" />
                    Start Strong
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Begin with words containing common vowels (A, E, I, O, U) and frequent consonants (R, S, T, L, N). 
                    Words like CRANE or SLATE maximize information from your first guess.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-primary" />
                    Eliminate Fast
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    If your first guess reveals little information, use your second guess to test five completely new letters. 
                    Aggressive elimination narrows possibilities faster than trying to position one purple tile.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Share2 className="w-5 h-5 text-primary" />
                    Watch Patterns
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Recognize common letter combinations like TH-, -ING, -ED, or QU. Pay attention to vowel placement patterns 
                    and don't forget that letters can appear twice in a word.
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Daily Challenge */}
          <section>
            <h2 className="text-3xl font-bold text-foreground mb-6">The Daily Challenge</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  New Puzzle Every Day
                </h3>
                <p className="text-muted-foreground mb-4">
                  A fresh puzzle drops at midnight in your timezone. Everyone worldwide gets the same puzzle, creating a shared 
                  experience that connects millions of word game enthusiasts.
                </p>
                <p className="text-muted-foreground">
                  This daily ritual transforms WordPop from a simple game into a morning routine. Whether you solve it over coffee, 
                  during your commute, or before bed, the daily format creates a satisfying habit that keeps you coming back.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-primary" />
                  Share Your Results
                </h3>
                <p className="text-muted-foreground mb-4">
                  After solving, share your colored grid pattern without spoiling the answer. Compare strategies with friends, 
                  celebrate efficient solves, or commiserate over particularly tricky puzzles.
                </p>
                <div className="bg-muted p-4 rounded-lg font-mono text-sm">
                  WordPop #123 — 3/6<br/>
                  <br/>
                  🟪🤎🤎🟪🤎<br/>
                  🩷🩷🤎🩷🤎<br/>
                  🩷🩷🩷🩷🩷
                </div>
              </div>
            </div>
          </section>

          {/* PWA Benefits - Enhanced with prominent CTA */}
          <section className="bg-gradient-to-br from-primary/10 via-accent/5 to-primary/5 border-2 border-primary/20 rounded-lg p-8 shadow-lg">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                <Smartphone className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-3xl font-bold text-foreground mb-2">
                Install WordPop for the Best Experience
              </h2>
              <p className="text-lg text-muted-foreground">
                Get instant loading, offline play, and a native app feel—no app store required
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-background/50 backdrop-blur-sm rounded-lg p-4 border border-border">
                <h3 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-primary" />
                  Lightning Fast
                </h3>
                <p className="text-sm text-muted-foreground">
                  Instant loading with cached assets (~1 second launch time). No waiting for the page to load—just tap and play.
                </p>
              </div>
              <div className="bg-background/50 backdrop-blur-sm rounded-lg p-4 border border-border">
                <h3 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-primary" />
                  Works Offline
                </h3>
                <p className="text-sm text-muted-foreground">
                  Play anytime, anywhere—even without internet. Your progress syncs automatically when you reconnect.
                </p>
              </div>
              <div className="bg-background/50 backdrop-blur-sm rounded-lg p-4 border border-border">
                <h3 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-primary" />
                  Home Screen Access
                </h3>
                <p className="text-sm text-muted-foreground">
                  Add WordPop to your home screen for quick access. Feels like a native app but works everywhere.
                </p>
              </div>
              <div className="bg-background/50 backdrop-blur-sm rounded-lg p-4 border border-border">
                <h3 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-primary" />
                  Full-Screen Mode
                </h3>
                <p className="text-sm text-muted-foreground">
                  Full-screen experience without browser bars. Maximum focus on the game itself.
                </p>
              </div>
            </div>
            
            {!isInstalled && (
              <div className="text-center space-y-3">
                <Button onClick={handleInstallClick} size="lg" className="px-8">
                  {canInstall ? "Install Now" : "View Installation Guide"}
                </Button>
                <p className="text-xs text-muted-foreground">
                  Takes ~30 seconds • No app store required • Works on iPhone, Android, and Desktop
                </p>
              </div>
            )}
            
            {isInstalled && (
              <div className="text-center p-4 bg-primary/10 rounded-lg border border-primary/20">
                <p className="font-semibold text-foreground flex items-center justify-center gap-2">
                  <Trophy className="w-5 h-5 text-primary" />
                  Already Installed! You're all set.
                </p>
              </div>
            )}
          </section>

          {/* Streaks & Stats */}
          <section>
            <h2 className="text-3xl font-bold text-foreground mb-6">Track Your Progress</h2>
            <p className="text-lg text-muted-foreground mb-6">
              WordPop tracks your performance with detailed statistics that help you improve over time.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Win Streaks</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Maintain daily solving streaks and challenge yourself to build longer runs. Track your current streak 
                    and personal best.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Guess Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    See how many attempts you typically need. Improve your average by developing better strategies and 
                    pattern recognition.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Win Rate</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Monitor your success percentage over time. Watch your skills improve as you learn optimal opening 
                    words and elimination techniques.
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Archive */}
          <section>
            <h2 className="text-3xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Archive className="w-8 h-8 text-primary" />
              Complete Puzzle Archive
            </h2>
            <p className="text-lg text-muted-foreground mb-4">
              Missed yesterday's puzzle? Want to practice? WordPop's archive gives you access to every previous daily puzzle. 
              Unlike games that lock past content, we believe you should be able to play at your own pace.
            </p>
            <p className="text-muted-foreground mb-6">
              The calendar-based archive shows which puzzles you've completed, making it easy to catch up on missed days or 
              challenge yourself with puzzles from weeks or months ago. Your archive progress is tracked separately from 
              your daily streak.
            </p>
            <Link to="/archive">
              <Button variant="outline">
                Browse Archive →
              </Button>
            </Link>
          </section>

          {/* Mobile Instructions */}
          <section className="bg-card border border-border rounded-lg p-8">
            <h2 className="text-3xl font-bold text-foreground mb-6">Play on Any Device</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-3">On iPhone/iPad</h3>
                <ol className="space-y-2 text-muted-foreground">
                  <li>1. Open WordPop in Safari</li>
                  <li>2. Tap the Share button (square with arrow)</li>
                  <li>3. Scroll down and tap "Add to Home Screen"</li>
                  <li>4. Tap "Add" in the top right</li>
                  <li>5. Find the WordPop icon on your home screen!</li>
                </ol>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-3">On Android</h3>
                <ol className="space-y-2 text-muted-foreground">
                  <li>1. Open WordPop in Chrome</li>
                  <li>2. Tap the menu icon (three dots)</li>
                  <li>3. Tap "Install app" or "Add to Home screen"</li>
                  <li>4. Confirm by tapping "Install"</li>
                  <li>5. Launch from your home screen or app drawer!</li>
                </ol>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section>
            <h2 className="text-3xl font-bold text-foreground mb-6">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="w-full">
              {faqData.map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <div className="mt-6 text-center">
              <Link to="/help">
                <Button variant="outline">
                  Full Help Guide →
                </Button>
              </Link>
            </div>
          </section>

          {/* Final CTA */}
          <section className="text-center py-12 bg-gradient-to-b from-primary/5 to-background rounded-lg border border-border">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Ready to Play?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Today's puzzle is waiting. Join thousands of players solving the daily WordPop challenge.
            </p>
            <Link to="/play">
              <Button size="lg" className="text-lg px-8 py-6">
                Play Today's Puzzle →
              </Button>
            </Link>
          </section>
        </main>

        <ColorLegend />
        <Footer />
        
        {/* Install Modal */}
        <InstallModal 
          open={showInstallModal}
          onOpenChange={setShowInstallModal}
          isOnline={navigator.onLine}
        />
      </div>
    </>
  );
}
